import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  RefreshCw,
  Building2,
  TrendingUp,
  Download,
  Filter,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { toast } from 'react-toastify';
import apiClient from '../../../api/client';
import BlurredValue from '../../../components/common/BlurredValue';
import { useFiscalPeriod } from '../../../contexts/FiscalPeriodContext';
import { formatDateToISO } from '../../../utils/formatters';
import LoadingSpinner from '../../../components/common/LoadingSpinner';

/**
 * Wholesale VAT Tracking Page
 * Displays internal VAT tracking for orders from non-Free Trade Zone companies
 * VAT is already included in prices - this page shows extracted VAT for accounting
 */
const WholesaleVATTrackingPage = () => {
  const navigate = useNavigate();
  const { currentPeriod } = useFiscalPeriod();

  // State
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [periodData, setPeriodData] = useState([]);
  const [total, setTotal] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [expandedCompany, setExpandedCompany] = useState(null);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  // Default dates: start of current month to today
  const getDefaultDates = () => {
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    return {
      from: formatDateToISO(startOfMonth),
      to: formatDateToISO(today)
    };
  };

  const defaultDates = getDefaultDates();

  // Filters - use fiscal period if available, otherwise defaults
  const [dateFrom, setDateFrom] = useState(currentPeriod?.startDate || defaultDates.from);
  const [dateTo, setDateTo] = useState(currentPeriod?.endDate || defaultDates.to);
  const [companyId, setCompanyId] = useState('');
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);

  // Update dates when fiscal period changes
  useEffect(() => {
    if (currentPeriod?.startDate && currentPeriod?.endDate) {
      setDateFrom(currentPeriod.startDate);
      setDateTo(currentPeriod.endDate);
    }
  }, [currentPeriod]);

  // Fetch VAT data
  const fetchVATData = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (companyId) params.append('company_id', companyId);
      params.append('limit', limit.toString());
      params.append('offset', offset.toString());

      // Fetch report data (includes summary and orders)
      const reportRes = await apiClient.get(`/wholesale/vat-tracking/report?${params.toString()}`);

      if (reportRes.data?.success) {
        setSummary(reportRes.data.data.summary);
        setOrders(reportRes.data.data.orders);
        setTotal(reportRes.data.data.total);
      }

      // Fetch period summary for charts
      const yearParam = dateFrom ? new Date(dateFrom).getFullYear() : new Date().getFullYear();
      const periodsRes = await apiClient.get(`/wholesale/vat-tracking/periods?year=${yearParam}`);

      if (periodsRes.data?.success) {
        setPeriodData(periodsRes.data.data);
      }
    } catch (error) {
      console.error('Error fetching VAT data:', error);
      toast.error('Failed to load VAT tracking data');
    } finally {
      setLoading(false);
      setInitialLoadDone(true);
    }
  };

  // Fetch on mount and when filters change
  useEffect(() => {
    fetchVATData();
  }, [dateFrom, dateTo, companyId, limit, offset]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-NG', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Handle export
  const handleExport = () => {
    // Build CSV
    let csv =
      'Order Number,Order Date,Company,Distributor,Order Total,VAT Amount,VAT Rate,Status\n';
    orders.forEach((order) => {
      csv += `"${order.order_number}","${formatDate(order.order_date)}","${order.company_name}","${order.distributor_name}","${order.total_amount}","${order.internal_vat_amount}","${order.internal_vat_rate}%","${order.order_status}"\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `vat_tracking_${dateFrom}_${dateTo}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('VAT report exported');
  };

  if (loading && !summary) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">VAT Tracking</h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              Internal VAT tracking from non-FTZ company orders (VAT inclusive in prices)
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            <Filter size={16} />
            Filters
            {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          <button
            onClick={handleExport}
            disabled={orders.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
          >
            <Download size={16} />
            Export
          </button>
          <button
            onClick={fetchVATData}
            disabled={loading}
            className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                From Date
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 p-2 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                To Date
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 p-2 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Results Per Page
              </label>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(parseInt(e.target.value));
                  setOffset(0);
                }}
                className="w-full border border-gray-300 dark:border-gray-600 p-2 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-5 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-sm">Total VAT Tracked</p>
                <p className="text-2xl font-bold mt-1">
                  <BlurredValue value={formatCurrency(summary.total_vat_amount)} />
                </p>
              </div>
              <TrendingUp className="w-10 h-10 text-emerald-200/50" />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-5">
            <p className="text-gray-500 dark:text-gray-400 text-sm">Total Orders</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
              {summary.total_orders}
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-5">
            <p className="text-gray-500 dark:text-gray-400 text-sm">Order Value</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
              <BlurredValue value={formatCurrency(summary.total_order_value)} />
            </p>
          </div>
        </div>
      )}

      {/* Company Breakdown */}
      {summary?.by_company && summary.by_company.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Building2 size={20} />
            VAT by Company
          </h2>
          <div className="space-y-3">
            {summary.by_company.map((company) => (
              <div
                key={company.company_id}
                className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {company.company_name}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {company.company_code} • {company.order_count} orders
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-emerald-600 dark:text-emerald-400">
                      <BlurredValue value={formatCurrency(company.total_vat_amount)} />
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      from <BlurredValue value={formatCurrency(company.total_order_value)} />
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Monthly Summary */}
      {periodData.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Calendar size={20} />
            Monthly VAT Summary
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {periodData.map((period) => (
              <div
                key={period.period}
                className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-center"
              >
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {period.period}
                </p>
                <p className="text-lg font-bold text-gray-900 dark:text-white mt-1">
                  <BlurredValue value={formatCurrency(period.total_vat)} />
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {period.order_count} orders
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Orders Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Order Details</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Showing {orders.length} of {total} orders with VAT
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Order
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Company
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Distributor
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Order Total
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  VAT Amount
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Rate
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {orders.map((order) => (
                <tr key={order.order_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-3">
                    <Link
                      to={`/wholesale/orders/${order.order_id}`}
                      className="text-emerald-600 dark:text-emerald-400 hover:underline font-medium"
                    >
                      {order.order_number}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                    {formatDate(order.order_date)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                    {order.company_name}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                    {order.distributor_name}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">
                    <BlurredValue value={formatCurrency(order.total_amount)} />
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-medium text-emerald-600 dark:text-emerald-400">
                    <BlurredValue value={formatCurrency(order.internal_vat_amount)} />
                  </td>
                  <td className="px-4 py-3 text-sm text-center text-gray-600 dark:text-gray-400">
                    {order.internal_vat_rate}%
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        order.payment_status === 'paid'
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : order.payment_status === 'partial'
                            ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                            : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400'
                      }`}
                    >
                      {order.payment_status}
                    </span>
                  </td>
                </tr>
              ))}
              {orders.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-8 text-center text-gray-500 dark:text-gray-400"
                  >
                    No orders with VAT found for the selected period
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > limit && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Showing {offset + 1} to {Math.min(offset + limit, total)} of {total}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setOffset(Math.max(0, offset - limit))}
                disabled={offset === 0}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setOffset(offset + limit)}
                disabled={offset + limit >= total}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <p className="text-sm text-blue-800 dark:text-blue-200">
          <strong>Note:</strong> This page shows internal VAT tracking for orders from companies
          outside the Free Trade Zone. VAT is already included in product prices. The amounts shown
          are extracted for accounting purposes only and do not appear on customer-facing documents
          (invoices, receipts, or proforma invoices).
        </p>
      </div>
    </div>
  );
};

export default WholesaleVATTrackingPage;
